<script lang="ts">
import CompanySkill from "@/Components/CompanySkill.vue";
import CarouselButton from "@/Components/Generics/CarouselButton.vue";
import GenericButton from "@/Components/Generics/GenericButton.vue";
import Icon from "@/Components/Generics/Icon.vue";
import TransitionHeight from "@/Components/Generics/TransitionHeight.vue";
import ScrollUtilities from "@/Helpers/ScrollUtilities";
import { QualificationGroup, QualificationId, QualificationInput, QualificationsGroups, QualificationValue } from "@/Types";
import { CompanyRequirement } from "@/Types/company";
import { PropType } from "vue";
import { defineComponent } from "vue";

export default defineComponent({
    components: {
        CompanySkill,
        CarouselButton,
        GenericButton,
        TransitionHeight,
        Icon,
    },
    props: {
        qualificationGroups: {
            type: Array as PropType<QualificationsGroups>,
            required: true,
        },
        qualificationInput: {
            type: Array as PropType<QualificationInput>,
            default: null,
        },
        companyRequirements: {
            type: Array as PropType<CompanyRequirement[]>,
            default: null,
        },
        isCompany: {
            type: Boolean,
            default: false,
        },
        create: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["qualificationValueChanged", "createValueChanged"],
    data: () => ({
        selectedIndex: 0,

        waitingForAnimationEnd: false,
    }),
    computed: {
        currentGroup(): QualificationGroup | null {
            if (!this.qualificationGroups) return null;

            return this.qualificationGroups[this.selectedIndex];
        },
    },
    methods: {
        clickNextCarousel(): void {
            if (this.selectedIndex >= this.qualificationGroups.length - 1) {
                ScrollUtilities.jumpToDivCenter("startOfFindCompany", undefined, true);
            } else {
                (this.$refs.refCarouselButton as InstanceType<typeof CarouselButton>)?.clickRight();
                this.scrollToSkills();
            }
        },
        clickPrevCarousel(): void {
            if (this.selectedIndex <= 0) {
                ScrollUtilities.jumpToDivCenter("heroPlane");
            } else {
                (this.$refs.refCarouselButton as InstanceType<typeof CarouselButton>)?.clickLeft();
                this.scrollToSkills();
            }
        },
        changeValue(value: number): void {
            this.selectedIndex = value;

            this.waitingForAnimationEnd = true;
        },

        getQualificationValueById(qualificationId: QualificationId): QualificationValue {
            if (!this.qualificationInput || this.qualificationInput.length < 1) return null;

            return this.qualificationInput.find((i) => i.id === qualificationId) ?? null;
        },
        getCompanyRequirementForQualificationId(qualificationId: QualificationId): CompanyRequirement {
            if (!this.companyRequirements?.length) return null;

            return this.companyRequirements.find((c_req) => c_req.qualification_id === qualificationId);
        },

        scrollToSkills(): void {
            if (!this.waitingForAnimationEnd) return;

            this.waitingForAnimationEnd = false;

            ScrollUtilities.jumpToDivCenter("startOfSkills");
        },
    },
});
</script>

<template>
    <div id="startOfSkills" class="flex flex--width flex--v flex--valign-c flex--gap-30">
        <CarouselButton ref="refCarouselButton" class="carousel-button" :selected-index="selectedIndex" :values="qualificationGroups" @change-value="changeValue">
            <template #title="{ selectedItem }">
                {{ selectedItem ? $trans("qualifications." + selectedItem.name) + ` ( ${selectedIndex + 1} / ${qualificationGroups.length} )` : "No item" }}
            </template>
        </CarouselButton>

        <div v-if="isCompany" class="slider-legend flex flex--width flex--v flex--valign-e flex--gap-5">
            <div class="slider-legend-entry flex flex--valign-c flex--gap-20">
                <span class="text text--size-18 text--accent-gray-b text--medium text--right">
                    {{ $trans("qualifications.your_choice") }}
                </span>
                <div class="slider-legend-blob slider-legend-blob--primary"></div>
            </div>

            <div class="slider-legend-entry flex flex--valign-c flex--gap-20">
                <span class="text text--size-18 text--accent-gray-b text--medium text--right">
                    {{ $trans("qualifications.requirements") }}
                </span>
                <div class="slider-legend-blob slider-legend-blob--secondary"></div>
            </div>

            <div class="slider-legend-entry flex flex--valign-c flex--gap-20">
                <span class="text text--size-18 text--accent-gray-b text--medium text--right">
                    {{ $trans("qualifications.important") }}
                </span>
                <Icon class="slider-legend-icon" :identifier="'djf_update_star'" />
            </div>
        </div>

        <TransitionHeight v-if="currentGroup" class="full-width">
            <div v-if="currentGroup" class="flex flex--v flex--valign-c flex--width flex--gap-15">
                <CompanySkill
                    v-for="(qualification, idx) in currentGroup.qualifications"
                    :key="idx"
                    :qualification="qualification"
                    :qualification-value="getQualificationValueById(qualification.id)"
                    :company-requirement="getCompanyRequirementForQualificationId(qualification.id)"
                    :company="isCompany"
                    :create="create"
                    @qualification-value-changed="(val) => $emit('qualificationValueChanged', qualification.id, val, false)"
                    @create-value-changed="(val, important) => $emit('createValueChanged', qualification.id, val, important)" />
            </div>
        </TransitionHeight>

        <div class="carousel-bottom-buttons flex flex--valign-c flex--gap-20">
            <GenericButton class="carousel-bottom-button" :title="$trans('main.button_back')" :circular="true" :simple="true" :uppercase="true" :disabled="(isCompany || create) && selectedIndex <= 0" @click="clickPrevCarousel" />

            <GenericButton
                class="carousel-bottom-button"
                :title="selectedIndex >= qualificationGroups.length - 1 && !isCompany && !create ? $trans('main.button_done') : $trans('main.button_continue')"
                :circular="true"
                :uppercase="true"
                :disabled="(isCompany || create) && selectedIndex >= qualificationGroups.length - 1"
                @click="clickNextCarousel" />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.carousel-button {
    max-width: 100%;
}

.carousel-bottom-buttons {
    .carousel-bottom-button {
        width: rem(300);
    }
}

.slider-legend {
    .slider-legend-entry {
        .slider-legend-blob {
            width: rem(30);
            height: rem(30);
            border-radius: 50%;

            &.slider-legend-blob--primary {
                background-color: $color-primary-b;
            }

            &.slider-legend-blob--secondary {
                background-color: $color-secondary-a;
            }
        }

        .slider-legend-icon {
            width: rem(30);
            height: rem(30);

            img {
                width: 100%;
                height: 100%;
                aspect-ratio: 1 / 1;
            }
        }
    }
}

@include media-breakpoint-down(lg) {
    .slider-legend {
        .slider-legend-entry {
            gap: rem(18);

            .slider-legend-blob {
                width: rem(25);
                height: rem(25);
            }

            .slider-legend-icon {
                width: rem(25);
                height: rem(25);
            }
        }
    }
}

@include media-breakpoint-down(md) {
    .carousel-bottom-buttons {
        flex-direction: column-reverse;
        gap: rem(10);
    }

    .slider-legend {
        .slider-legend-entry {
            gap: rem(15);

            .slider-legend-blob {
                width: rem(20);
                height: rem(20);
            }

            .slider-legend-icon {
                width: rem(20);
                height: rem(20);
            }
        }
    }
}

@include media-breakpoint-down(sm) {
    .carousel-bottom-buttons {
        .carousel-bottom-button {
            width: rem(230);
        }
    }

    .slider-legend {
        .slider-legend-entry {
            gap: rem(10);

            .slider-legend-blob {
                width: rem(15);
                height: rem(15);
            }

            .slider-legend-icon {
                width: rem(15);
                height: rem(15);
            }
        }
    }
}
</style>
